import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { CircleAlert } from "lucide-react";

const TOOLTIP_MAX_WIDTH = 320;
const VIEWPORT_MARGIN = 8;
const TOOLTIP_OFFSET = 6;
const MIN_SPACE_ABOVE = 96;

const getTooltipStyle = (element) => {
  const rect = element.getBoundingClientRect();
  const width = Math.min(TOOLTIP_MAX_WIDTH, window.innerWidth - (VIEWPORT_MARGIN * 2));
  const left = Math.max(VIEWPORT_MARGIN, Math.min(rect.left, window.innerWidth - width - VIEWPORT_MARGIN));
  const above = rect.top >= MIN_SPACE_ABOVE;

  return {
    left,
    maxWidth: width,
    top: above ? rect.top - TOOLTIP_OFFSET : rect.bottom + TOOLTIP_OFFSET,
    transform: above ? "translateY(-100%)" : undefined,
  };
};

const ValidationMessage = ({ className, id, message }) => {
  const messageRef = useRef(null);
  const textRef = useRef(null);
  const [truncated, setTruncated] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState(null);

  const measure = useCallback(() => {
    const text = textRef.current;
    if (text) setTruncated(text.scrollWidth > text.clientWidth);
  }, []);

  useLayoutEffect(measure, [measure, message]);

  useEffect(() => {
    if (typeof ResizeObserver === "undefined" || !textRef.current) return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(textRef.current);
    return () => observer.disconnect();
  }, [measure]);

  const open = () => {
    if (truncated && messageRef.current) setTooltipStyle(getTooltipStyle(messageRef.current));
  };
  const close = useCallback(() => setTooltipStyle(null), []);
  const visible = truncated && tooltipStyle !== null;

  useEffect(() => {
    if (!visible) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") close();
    };
    // The tooltip is fixed to the viewport, so it would drift away from the message if the page moved underneath it.
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [close, visible]);

  return (
    <p
      className={[className, "wsm-validation-message", truncated && "wsm-validation-message--truncated"].filter(Boolean).join(" ")}
      id={id}
      onMouseEnter={open}
      onMouseLeave={close}
      ref={messageRef}
      role="alert"
    >
      {truncated ? (
        <button
          aria-label="Show full validation message"
          className="wsm-validation-message__trigger"
          onBlur={close}
          onClick={open}
          onFocus={open}
          type="button"
        >
          <CircleAlert aria-hidden="true" />
        </button>
      ) : <CircleAlert aria-hidden="true" />}
      <span className="wsm-validation-message__text" ref={textRef}>{message}</span>
      {visible && <span aria-hidden="true" className="wsm-validation-message__tooltip" style={tooltipStyle}>{message}</span>}
    </p>
  );
};

ValidationMessage.propTypes = {
  className: PropTypes.string,
  id: PropTypes.string,
  message: PropTypes.string.isRequired,
};

export default ValidationMessage;
